/**
 * @fileoverview Notificações: avisos do navegador (conversas e chat interno) e o
 * alerta por SMS de conversas sem resposta (SMSReportSchedulerService no
 * backend). Os mesmos campos do SMS também são editados em Multi unidades.
 */

import React, { useState } from "react";
import { CircularProgress, Collapse, MenuItem, TextField } from "@material-ui/core";
import { Send } from "lucide-react";
import { toast } from "react-toastify";

import SettingsSection from "../../../components/Settings/SettingsSection";
import SettingItem from "../../../components/Settings/SettingItem";
import ModernSelect from "../../../components/ModernSelect";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import { formatPhoneDisplay, sanitizePastedPhone } from "../../../utils/phoneInput";
import { bindSetting, useDraftSetting } from "../useSettingsArea";
import useSectionStyles from "./sectionStyles";

const INTERVAL_UNITS = ["minutes", "hours", "days"];
const t = (key, options) => i18n.t(`settings.sections.notifications.${key}`, options);

// Lista salva (JSON com só dígitos) ↔ texto da tela (um número formatado por linha).
// Cada linha é um número inteiro, então "55 99698-8996" é DDD 55 + número.
export const phonesToText = (json) => {
  try {
    const phones = JSON.parse(json || "[]");
    return Array.isArray(phones) ? phones.map((phone) => formatPhoneDisplay(phone)).join("\n") : "";
  } catch (err) {
    return "";
  }
};

export const textToPhones = (text) =>
  JSON.stringify(
    String(text || "")
      .split("\n")
      .map((line) => sanitizePastedPhone(line))
      .filter(Boolean)
  );

// Ligar os avisos pede a permissão do navegador de quem está ligando.
const askBrowserPermission = async () => {
  if (!("Notification" in window)) {
    toast.error(t("toasts.unsupported"));
    return false;
  }
  let permission = Notification.permission;
  if (permission !== "granted") permission = await Notification.requestPermission();
  if (permission !== "granted") {
    toast.warn(t("toasts.permissionDenied"));
    return false;
  }
  return true;
};

const SmsAlert = ({ area }) => {
  const classes = useSectionStyles();
  const threshold = useDraftSetting(area, "smsAlertThreshold", { fallback: "20" });
  const intervalValue = useDraftSetting(area, "smsAlertIntervalValue", { fallback: "20" });
  const phones = useDraftSetting(area, "smsReportPhones", {
    fallback: "[]",
    fromValue: phonesToText,
    toValue: textToPhones,
  });
  const [testing, setTesting] = useState(false);

  const handlePhonesBlur = () => {
    phones.setValue(phonesToText(textToPhones(phones.value)));
    phones.flush();
  };

  // O teste vai para o 1º número salvo: grava a lista antes de testar.
  const handleTest = async () => {
    setTesting(true);
    try {
      await phones.flush();
      const { data } = await api.post("/sms-report/test");
      if (data.success) toast.success(t("fields.smsTest.sent", { phone: formatPhoneDisplay(data.phone) }));
      else toast.error(t("fields.smsTest.failed", { error: data.error || "" }));
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <SettingItem
        id="smsThreshold"
        divider
        wide
        title={t("fields.smsThreshold.title")}
        description={t("fields.smsThreshold.description")}
        loading={threshold.saving}
        saved={threshold.saved}
      >
        <TextField
          type="number"
          size="small"
          variant="outlined"
          value={threshold.value}
          onChange={threshold.onChange}
          onBlur={threshold.flush}
          inputProps={{ min: 1, max: 100, "aria-labelledby": "setting-smsThreshold-title" }}
          className={classes.numberField}
        />
      </SettingItem>

      <SettingItem
        id="smsInterval"
        wide
        title={t("fields.smsInterval.title")}
        description={t("fields.smsInterval.description")}
        loading={intervalValue.saving || !!area.saving.smsAlertIntervalUnit}
        saved={Math.max(intervalValue.saved || 0, area.saved.smsAlertIntervalUnit || 0) || undefined}
      >
        <div className={classes.inline}>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            value={intervalValue.value}
            onChange={intervalValue.onChange}
            onBlur={intervalValue.flush}
            inputProps={{ min: 1, "aria-labelledby": "setting-smsInterval-title" }}
            className={classes.numberField}
          />
          <ModernSelect
            size="small"
            className={classes.unitSelect}
            value={area.get("smsAlertIntervalUnit", "minutes")}
            onChange={(event) => area.save("smsAlertIntervalUnit", event.target.value)}
            MenuProps={{ disableScrollLock: true }}
            SelectDisplayProps={{ "aria-label": t("fields.smsInterval.unit") }}
          >
            {INTERVAL_UNITS.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {t(`fields.smsInterval.units.${unit}`)}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>
      </SettingItem>

      <SettingItem
        id="smsPhones"
        stacked
        title={t("fields.smsPhones.title")}
        description={t("fields.smsPhones.description")}
        loading={phones.saving}
        saved={phones.saved}
      >
        <TextField
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          value={phones.value}
          onChange={phones.onChange}
          onBlur={handlePhonesBlur}
          placeholder={"+55 (11) 99999-9999\n+55 (21) 98888-8888"}
          className={classes.field}
          inputProps={{ "aria-labelledby": "setting-smsPhones-title" }}
        />
      </SettingItem>

      <SettingItem id="smsTest" title={t("fields.smsTest.title")} description={t("fields.smsTest.description")}>
        <button type="button" className={classes.secondaryButton} onClick={handleTest} disabled={testing}>
          {testing ? <CircularProgress size={14} color="inherit" /> : <Send size={14} strokeWidth={1.8} />}
          {testing ? t("fields.smsTest.sending") : t("fields.smsTest.button")}
        </button>
      </SettingItem>
    </>
  );
};

const Notifications = ({ area }) => {
  const smsEnabled = area.get("smsAlertEnabled", "disabled") === "enabled";

  const handleWebNotifications = async (value) => {
    if (value === "enabled" && !(await askBrowserPermission())) return;
    area.save("webNotifications", value);
  };

  return (
    <>
      <SettingsSection title={t("groups.browser.title")} description={t("groups.browser.description")}>
        <SettingItem
          toggle
          {...bindSetting(area, "webNotifications", "disabled")}
          onChange={handleWebNotifications}
          title={t("fields.webNotifications.title")}
          description={t("fields.webNotifications.description")}
        />
        <SettingItem
          toggle
          {...bindSetting(area, "internalChatNotifications", "enabled")}
          title={t("fields.internalChatNotifications.title")}
          description={t("fields.internalChatNotifications.description")}
        />
      </SettingsSection>

      <SettingsSection title={t("groups.sms.title")} description={t("groups.sms.description")}>
        <SettingItem
          toggle
          {...bindSetting(area, "smsAlertEnabled", "disabled")}
          id="smsAlert"
          title={t("fields.smsAlert.title")}
          description={t("fields.smsAlert.description")}
        />
        <Collapse in={smsEnabled} unmountOnExit>
          <SmsAlert area={area} />
        </Collapse>
      </SettingsSection>
    </>
  );
};

export default Notifications;
